import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Select, SelectContentUltraPriority, SelectItem, SelectTrigger } from '@/components/ui/select';
import {
  calcularTotalGB,
  calcularTotalValor,
  type ComposicaoProposta,
  type LinhaComposicao,
  type PlanoCNPJ,
} from '@/constants/planosCNPJ';
import { usePlanosPropostaCatalogo, type PlanoCatalogoCategoria } from '@/hooks/usePlanosPropostaCatalogo';
import { normalizarMsisdn } from '@/lib/renovacaoCalculo';
import {
  ROTULO_SERVICO,
  SERVICOS_SIMULADOS,
  equivalenteNoModo,
  mesesAteFimDoDesconto,
  modoDoCliente,
  planoServeAoModo,
  sugerirPlanoFixa,
  valorDepoisDoDesconto,
  type ItemContaFixa,
  type ModoPreco,
  type ServicoFixa,
} from '@/lib/contaFixa';
import { cn } from '@/lib/utils';

// ⛔ A RENOVAÇÃO DO LEAD CPF (Douglas 25/09/2026, mockup aprovado).
// A conta vem da gerente Claro ou do PDF anexado; a simulação só MOSTRA — o
// consultor leva para a proposta clicando. Sem TM, sem faixa, sem comissão.
//
// ⛔ SIMULADOR ÚNICO (Douglas 27/09/2026, mockup aprovado): a conta fixa
// (fotos da gerente) e a móvel (PDF) chegam separadas e entram numa simulação
// só, com o preço "no Multi" quando fibra e móvel ficam juntos.

type LinhaLida = {
  msisdn?: string;
  situacao_atual?: {
    plano?: string;
    plano_nome?: string;
    valor?: number;
    valor_mensal?: number;
    uso_gb?: number;
    gb_nominal?: number;
    data_fidelidade?: string | null;
    fidelizado?: boolean;
    servicos?: string[];
    itens_detalhados?: Array<{ descricao?: string; valor?: number }>;
  };
};

type ContaLida = {
  fatura_id: string;
  tipo_conta?: 'MOVEL' | 'FIXA';
  conta_numero: string | null;
  titular: string | null;
  periodo_inicio: string | null;
  periodo_fim: string | null;
  vencimento: string | null;
  total: number | null;
  linhas: LinhaLida[];
  // Só na conta fixa
  lida?: boolean;
  confere?: boolean | null;
  paginas?: string[];
  itens?: ItemContaFixa[];
};

const RETIRAR = '__retirar__';

type LinhaConta = {
  numero: string;
  faturaId: string;
  contaNumero: string | null;
  plano: string;
  valor: number;
  usoGb: number;
  franquiaGb: number;
  fidelidade: string | null;
  servicos: string[];
};

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const corrigirTexto = (valor: string) => {
  try {
    return /Ã|Â/.test(valor) ? decodeURIComponent(escape(valor)) : valor;
  } catch {
    return valor;
  }
};

const dataBr = (iso: string | null | undefined) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

const fmtTelefone = (valor: string) => {
  const d = normalizarMsisdn(valor);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return valor;
};

const fmtGb = (gb: number) => `${gb.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} GB`;

// "Controle 40 GB (35 GB + 5 GB bônus) · no Multi" → nome numa linha, franquia embaixo
const partirNomePlano = (nome: string) => {
  const m = nome.match(/^(.*?)\s*\(([^)]*)\)\s*(.*)$/);
  if (!m) return { nome, detalhe: null as string | null };
  return { nome: `${m[1]} ${m[3]}`.replace(/\s+/g, ' ').trim(), detalhe: m[2].trim() };
};

const linhasDaConta = (conta: ContaLida): LinhaConta[] =>
  (conta.linhas || [])
    .map((linha) => {
      const s = linha.situacao_atual || {};
      const adicionais = (s.itens_detalhados || [])
        .map((item) => String(item.descricao || ''))
        .filter((d) => /item adicional/i.test(d))
        .map((d) => d.replace(/\s*\(item adicional\)\s*/i, ''));
      return {
        numero: normalizarMsisdn(String(linha.msisdn || '')),
        faturaId: conta.fatura_id,
        contaNumero: conta.conta_numero,
        plano: corrigirTexto(String(s.plano_nome || s.plano || '')),
        valor: Number(s.valor_mensal ?? s.valor ?? 0),
        usoGb: Number(s.uso_gb || 0),
        franquiaGb: Number(s.gb_nominal || 0),
        fidelidade: s.data_fidelidade || null,
        servicos: [...(s.servicos || []), ...adicionais].map(corrigirTexto),
      };
    })
    .filter((linha) => linha.numero.length >= 10);

// O menor plano que cobre o consumo real com folga; sem consumo, o que mantém a franquia.
function sugerirPlano(linha: LinhaConta, planos: PlanoCNPJ[]): PlanoCNPJ | null {
  const ordenados = [...planos].sort((a, b) => Number(a.valor) - Number(b.valor));
  const minimo = linha.usoGb > 0 ? Math.max(linha.usoGb * 1.5, 5) : linha.franquiaGb;
  return ordenados.find((plano) => Number(plano.gb || 0) >= minimo)
    ?? [...planos].sort((a, b) => Number(b.gb || 0) - Number(a.gb || 0))[0]
    ?? null;
}

interface Props {
  leadId: string;
  composicao: ComposicaoProposta | null;
  onComposicaoChange?: (composicao: ComposicaoProposta) => void;
}

const assinado = (dif: number) => `${dif > 0 ? '+' : dif < 0 ? '−' : ''}${moeda.format(Math.abs(dif))}`;
const corDif = (dif: number) => (dif > 0 ? 'text-success' : 'text-primary');

type PlanoCat = PlanoCNPJ & { nome: string; valor: number };

export function ContaRenovacaoCpf({ leadId, composicao, onComposicaoChange }: Props) {
  const { data: contas = [], isLoading } = useQuery({
    queryKey: ['renovacao-cpf-contas', leadId],
    enabled: !!leadId,
    staleTime: 60_000,
    queryFn: async (): Promise<ContaLida[]> => {
      const chamar = supabase.rpc.bind(supabase) as unknown as (
        fn: string, args: Record<string, string>,
      ) => Promise<{ data: unknown; error: Error | null }>;
      const { data, error } = await chamar('renovacao_cpf_contas', { p_lead_id: leadId });
      if (error) throw error;
      return Array.isArray(data) ? (data as ContaLida[]) : [];
    },
  });

  const { withCatalogo } = usePlanosPropostaCatalogo('PF');
  const planos = useMemo(
    () => withCatalogo('renov' as PlanoCatalogoCategoria, [] as PlanoCNPJ[]) as PlanoCat[],
    [withCatalogo],
  );
  const catalogoFixa = useMemo<Record<ServicoFixa, PlanoCat[]>>(() => ({
    INTERNET: withCatalogo('fibra', [] as PlanoCNPJ[]) as PlanoCat[],
    TV: withCatalogo('tv', [] as PlanoCNPJ[]) as PlanoCat[],
    FIXO: (withCatalogo('adicional_fibra', [] as PlanoCNPJ[]) as PlanoCat[]).filter((p) => /claro fixo/i.test(p.nome)),
    CELULAR: [], ADICIONAL: [], OUTRO: [],
  }), [withCatalogo]);

  const moveis = useMemo(() => contas.filter((c) => c.tipo_conta !== 'FIXA'), [contas]);
  const fixa = useMemo(() => contas.find((c) => c.tipo_conta === 'FIXA') ?? null, [contas]);
  const itensFixa = useMemo(
    () => (fixa?.lida ? (fixa.itens || []) : []).filter((i) => SERVICOS_SIMULADOS.includes(i.servico)),
    [fixa],
  );

  const linhas = useMemo(() => moveis.flatMap(linhasDaConta), [moveis]);
  const naProposta = useMemo(() => new Map(
    (composicao?.linhas || [])
      .filter((linha) => linha.tipo === 'RENOVACAO')
      .map((linha) => [normalizarMsisdn(String(linha.numero || '')), linha] as const),
  ), [composicao]);

  const [escolhas, setEscolhas] = useState<Record<string, string>>({});
  const [escolhasFixa, setEscolhasFixa] = useState<Partial<Record<ServicoFixa, string>>>({});

  // Sem conta fixa não se sabe se o cliente tem fibra: o simulador segue como
  // antes. Com ela, o preço acompanha o combo que sobra na proposta.
  const comFixa = itensFixa.length > 0;
  const temFibra = comFixa && itensFixa.some((i) => i.servico === 'INTERNET') && escolhasFixa.INTERNET !== RETIRAR;
  const modoMovel: ModoPreco | null = comFixa ? (temFibra ? 'COMBO' : 'SINGLE') : null;
  // O celular pode não ter conta móvel lida ainda: o total dele na conta fixa já prova que ele existe.
  const temMovel = linhas.length > 0 || (fixa?.itens || []).some((i) => i.servico === 'CELULAR' && Number(i.valor) > 0);
  const modoFixa: ModoPreco = modoDoCliente(temFibra, temMovel);
  const modoDoServico = (servico: ServicoFixa): ModoPreco =>
    servico === 'INTERNET' ? (temMovel ? 'COMBO' : 'SINGLE') : modoFixa;

  useEffect(() => {
    if (planos.length === 0 || linhas.length === 0) return;
    setEscolhas((atual) => {
      const proximo = { ...atual };
      for (const linha of linhas) {
        if (proximo[linha.numero]) continue;
        const salva = naProposta.get(linha.numero);
        const plano = salva?.planoId && planos.some((p) => p.id === salva.planoId)
          ? planos.find((p) => p.id === salva.planoId) ?? null
          : sugerirPlano(linha, planos) as PlanoCat | null;
        const noModo = plano && modoMovel ? equivalenteNoModo(plano, modoMovel, planos) : plano;
        if (noModo) proximo[linha.numero] = noModo.id;
      }
      return proximo;
    });
  }, [linhas, planos, naProposta, modoMovel]);

  useEffect(() => {
    if (itensFixa.length === 0) return;
    setEscolhasFixa((atual) => {
      const proximo = { ...atual };
      for (const item of itensFixa) {
        if (proximo[item.servico]) continue;
        const sugestao = sugerirPlanoFixa(item, catalogoFixa[item.servico], modoDoServico(item.servico));
        if (sugestao) proximo[item.servico] = sugestao.id;
      }
      return proximo;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itensFixa, catalogoFixa]);

  // Tirou ou devolveu a fibra: cada plano troca para a versão do novo combo.
  useEffect(() => {
    if (!modoMovel) return;
    setEscolhas((atual) => {
      const proximo: Record<string, string> = {};
      for (const [numero, id] of Object.entries(atual)) {
        const plano = planos.find((p) => p.id === id);
        proximo[numero] = plano ? equivalenteNoModo(plano, modoMovel, planos).id : id;
      }
      return proximo;
    });
    setEscolhasFixa((atual) => {
      const proximo = { ...atual };
      for (const servico of Object.keys(atual) as ServicoFixa[]) {
        const id = atual[servico];
        if (!id || id === RETIRAR) continue;
        const cat = catalogoFixa[servico];
        const plano = cat.find((p) => p.id === id);
        if (plano) proximo[servico] = equivalenteNoModo(plano, modoDoServico(servico), cat).id;
      }
      return proximo;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoMovel]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando a conta…
      </div>
    );
  }
  if (contas.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Nenhuma conta lida ainda. Anexe o PDF ou solicite a fatura à gerente — quando ela for lida, a conta e a simulação aparecem aqui.
      </p>
    );
  }

  const planoDe = (numero: string) => planos.find((p) => p.id === escolhas[numero]) || null;
  const planoFixaDe = (servico: ServicoFixa) => {
    const id = escolhasFixa[servico];
    if (!id || id === RETIRAR) return null;
    return catalogoFixa[servico].find((p) => p.id === id) || null;
  };
  const retirado = (servico: ServicoFixa) => escolhasFixa[servico] === RETIRAR;

  const atualMovel = linhas.reduce((s, l) => s + l.valor, 0);
  const novoMovel = linhas.reduce((s, l) => s + Number(planoDe(l.numero)?.valor || 0), 0);
  const atualFixa = itensFixa.reduce((s, i) => s + Number(i.valor || 0), 0);
  const depoisFixa = itensFixa.reduce((s, i) => s + valorDepoisDoDesconto(i), 0);
  const novoFixa = itensFixa.reduce((s, i) => s + (retirado(i.servico) ? 0 : Number(planoFixaDe(i.servico)?.valor || 0)), 0);
  const totalAtual = atualMovel + atualFixa;
  const totalNovo = novoMovel + novoFixa;
  const totalDepois = atualMovel + depoisFixa;
  const completaMovel = linhas.every((l) => planoDe(l.numero));
  const completaFixa = itensFixa.every((i) => retirado(i.servico) || planoFixaDe(i.servico));
  const completa = completaMovel && completaFixa;
  const itensComDesconto = itensFixa.filter((i) => mesesAteFimDoDesconto(i) != null);

  const fibraNaProposta = (composicao?.linhas || []).find((l) => l.tipo === 'FIBRA');
  const tvNaProposta = (composicao?.linhas || []).find((l) => l.tipo === 'TV');
  const fixaJaNaProposta = itensFixa.every((i) => {
    if (i.servico === 'FIXO') return true;
    const naLinha = i.servico === 'INTERNET' ? fibraNaProposta : tvNaProposta;
    return retirado(i.servico) ? !naLinha : naLinha?.planoId === escolhasFixa[i.servico];
  });
  const jaNaProposta = (linhas.length > 0 || itensFixa.length > 0) && fixaJaNaProposta && linhas.every((l) => {
    const salva = naProposta.get(l.numero);
    return salva && salva.planoId === escolhas[l.numero];
  });

  const levarParaProposta = () => {
    if (!onComposicaoChange || !completa) return;
    const numeros = new Set(linhas.map((l) => l.numero));
    const trocaFixa = itensFixa.length > 0;
    const outras = (composicao?.linhas || []).filter((linha) => {
      if (linha.tipo === 'RENOVACAO' && numeros.has(normalizarMsisdn(String(linha.numero || '')))) return false;
      if (trocaFixa && (linha.tipo === 'FIBRA' || linha.tipo === 'TV')) return false;
      return true;
    });
    const renovacoes: LinhaComposicao[] = linhas.map((l) => {
      const salva = naProposta.get(l.numero);
      const plano = planoDe(l.numero) as PlanoCNPJ;
      const valor = Number(plano.valor || 0);
      return {
        ...salva,
        id: salva?.id || `renovacao-cpf-${l.numero}`,
        linha_id: salva?.linha_id || crypto.randomUUID(),
        tipo: 'RENOVACAO',
        numero: l.numero,
        faturaUploadId: l.faturaId,
        contaNumero: l.contaNumero || undefined,
        gbAtual: l.franquiaGb,
        planoAtual: l.plano,
        valorAtual: l.valor,
        planoId: plano.id,
        plano,
        planoNovo: plano.nome,
        receitaRenovada: valor,
        receitaAdicionada: 0,
        ticketNovo: valor,
      } as LinhaComposicao;
    });
    const fixas: LinhaComposicao[] = [];
    const internet = planoFixaDe('INTERNET');
    const fixo = planoFixaDe('FIXO');
    if (internet) {
      const atualInternet = itensFixa.find((i) => i.servico === 'INTERNET');
      fixas.push({
        ...(fibraNaProposta ?? {}),
        id: fibraNaProposta?.id || `fibra-conta-${Date.now()}`,
        linha_id: fibraNaProposta?.linha_id || crypto.randomUUID(),
        numero: 'Fibra',
        tipo: 'FIBRA',
        tipoFibra: 'CNPJ',
        planoId: internet.id,
        plano: internet,
        planoAtual: atualInternet?.plano || undefined,
        valorAtual: atualInternet?.valor,
        ...(fixo ? { adicionais: { ...(fibraNaProposta?.adicionais || {}), [fixo.id]: 1 } } : {}),
      } as LinhaComposicao);
    }
    const tv = planoFixaDe('TV');
    if (tv) {
      const atualTv = itensFixa.find((i) => i.servico === 'TV');
      fixas.push({
        ...(tvNaProposta ?? {}),
        id: tvNaProposta?.id || `tv-conta-${Date.now()}`,
        linha_id: tvNaProposta?.linha_id || crypto.randomUUID(),
        numero: 'TV',
        tipo: 'TV',
        planoId: tv.id,
        plano: tv,
        planoAtual: atualTv?.plano || undefined,
        valorAtual: atualTv?.valor,
      } as LinhaComposicao);
    }
    const todas = [...renovacoes, ...fixas, ...outras];
    const incluiFibra = todas.some((linha) => linha.tipo === 'FIBRA');
    onComposicaoChange({
      ...(composicao ?? { economiaValor: 0, economiaPercent: 0 }),
      linhas: todas,
      incluiFibra,
      totalGB: calcularTotalGB(todas, incluiFibra),
      totalValor: calcularTotalValor(todas),
    } as ComposicaoProposta);
  };

  const th = 'px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
  const td = 'px-2 py-2.5 align-middle';
  const grupo = 'px-2 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground';

  const celularDaFixa = fixa?.lida ? (fixa.itens || []).find((i) => i.servico === 'CELULAR')?.valor ?? null : null;
  const totalMovelConta = moveis.reduce((s, c) => s + Number(c.total || 0), 0);
  const celularBate = celularDaFixa != null && moveis.length > 0
    ? Math.abs(Number(celularDaFixa) - totalMovelConta) <= 0.05
    : null;

  const seletorPlano = (
    valor: string,
    aoTrocar: (id: string) => void,
    opcoes: PlanoCat[],
    plano: PlanoCat | null,
    podeRetirar: boolean,
    modo: ModoPreco | null,
  ) => {
    const partes = plano ? partirNomePlano(plano.nome) : null;
    const foraDoModo = plano && modo && !planoServeAoModo(plano.nome, modo);
    return (
      <>
        <Select value={valor} onValueChange={aoTrocar}>
          <SelectTrigger className="h-auto min-h-9 py-1 text-left text-xs">
            {valor === RETIRAR ? (
              <span className="text-muted-foreground">Retirar</span>
            ) : partes ? (
              <span className="min-w-0">
                <span className="block truncate font-semibold" title={plano?.nome}>{partes.nome}</span>
                {partes.detalhe && <span className="block truncate text-[10.5px] text-muted-foreground">{partes.detalhe}</span>}
              </span>
            ) : <span className="text-muted-foreground">Escolher plano</span>}
          </SelectTrigger>
          <SelectContentUltraPriority>
            {podeRetirar && <SelectItem value={RETIRAR}>Retirar</SelectItem>}
            {opcoes.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.nome} · {moeda.format(Number(p.valor))}</SelectItem>
            ))}
          </SelectContentUltraPriority>
        </Select>
        {foraDoModo && (
          <span className="mt-0.5 block text-[10.5px] text-primary">
            {modo === 'COMBO' ? 'Preço sem combo' : 'Preço de combo — confira'}
          </span>
        )}
      </>
    );
  };

  return (
    <div className="space-y-3">
      {fixa && (
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm font-bold text-foreground">Conta fixa</p>
            {(fixa.paginas || []).length > 0 && (
              <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                Fotos:
                {(fixa.paginas || []).map((url, n) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{n + 1}</a>
                ))}
              </span>
            )}
          </div>
          {!fixa.lida ? (
            <p className="text-xs text-muted-foreground">
              Conta fixa recebida da gerente. A leitura das fotos roda sozinha em alguns minutos — a conta e a simulação aparecem aqui.
            </p>
          ) : (
            <>
              <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
                {[
                  { l: 'Titular', v: corrigirTexto(fixa.titular || '—') },
                  { l: 'Código da conta', v: fixa.conta_numero || '—' },
                  { l: 'Vencimento', v: dataBr(fixa.vencimento) },
                  { l: 'Total da conta', v: fixa.total == null ? '—' : moeda.format(Number(fixa.total)) },
                ].map((k) => (
                  <div key={k.l} className="rounded-lg bg-muted/40 px-3 py-2">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.l}</p>
                    <p className="truncate text-sm font-semibold text-foreground" title={k.v}>{k.v}</p>
                  </div>
                ))}
              </div>
              <table className="w-full table-fixed text-xs">
                <colgroup>
                  <col className="w-[14%]" /><col className="w-[34%]" /><col className="w-[13%]" />
                  <col className="w-[21%]" /><col className="w-[18%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className={th}>Serviço</th><th className={th}>Plano atual</th>
                    <th className={cn(th, 'text-right')}>Valor atual</th><th className={th}>Desconto</th>
                    <th className={th}>Fidelidade</th>
                  </tr>
                </thead>
                <tbody>
                  {(fixa.itens || []).map((i, n) => {
                    const faltam = mesesAteFimDoDesconto(i);
                    const fidelidadeVencida = i.fidelidade_ate ? new Date(i.fidelidade_ate) < new Date() : false;
                    return (
                      <tr key={`${i.servico}-${n}`} className="border-b border-border last:border-0">
                        <td className={cn(td, 'font-semibold')}>{ROTULO_SERVICO[i.servico]}</td>
                        <td className={td}>
                          <span className="block truncate" title={i.plano || ''}>{i.plano || '—'}</span>
                          {i.servico === 'CELULAR' && <span className="text-[10.5px] text-muted-foreground">linhas na conta móvel</span>}
                          {(i.servico === 'ADICIONAL' || i.servico === 'OUTRO') && <span className="text-[10.5px] text-muted-foreground">não entra na comparação</span>}
                          {i.valor_cheio != null && faltam != null && (
                            <span className="text-[10.5px] text-muted-foreground">cheio {moeda.format(Number(i.valor_cheio))}</span>
                          )}
                        </td>
                        <td className={cn(td, 'text-right tabular-nums')}>{moeda.format(Number(i.valor || 0))}</td>
                        <td className={cn(td, faltam != null ? 'text-primary' : 'text-muted-foreground')}>
                          {faltam != null ? (
                            <>
                              <span className="block">−{moeda.format(Number(i.desconto))} · parcela {i.parcela_atual} de {i.parcelas_total}</span>
                              <span className="text-[10.5px] text-muted-foreground">
                                {faltam === 0 ? 'última parcela' : `sobe para ${moeda.format(valorDepoisDoDesconto(i))} em ${faltam} ${faltam === 1 ? 'mês' : 'meses'}`}
                              </span>
                            </>
                          ) : '—'}
                        </td>
                        <td className={cn(td, i.fidelidade_ate ? 'text-primary' : 'text-muted-foreground')}>
                          {i.fidelidade_ate ? (fidelidadeVencida ? `venceu em ${dataBr(i.fidelidade_ate)}` : `até ${dataBr(i.fidelidade_ate)}`) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {fixa.confere === false && (
                <p className="mt-2 text-[11px] text-primary">A soma dos serviços lidos não fechou com o total da conta. Confira nas fotos.</p>
              )}
              {celularBate != null && (
                <p className={cn('mt-2 text-[11px]', celularBate ? 'text-muted-foreground' : 'text-primary')}>
                  {celularBate
                    ? `O celular da conta fixa (${moeda.format(Number(celularDaFixa))}) bate com a conta móvel.`
                    : `O celular da conta fixa (${moeda.format(Number(celularDaFixa))}) não bate com a conta móvel (${moeda.format(totalMovelConta)}).`}
                </p>
              )}
            </>
          )}
        </section>
      )}

      {moveis.map((conta) => {
        const doConta = linhasDaConta(conta);
        return (
          <section key={conta.fatura_id} className="rounded-xl border border-border bg-card p-4">
            <p className="mb-3 text-sm font-bold text-foreground">{fixa ? 'Conta móvel' : 'Conta'}</p>
            <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
              {[
                { l: 'Titular', v: corrigirTexto(conta.titular || '—') },
                { l: 'Período', v: `${dataBr(conta.periodo_inicio)} a ${dataBr(conta.periodo_fim)}` },
                { l: 'Vencimento', v: dataBr(conta.vencimento) },
                { l: 'Total da conta', v: conta.total == null ? '—' : moeda.format(Number(conta.total)) },
              ].map((k) => (
                <div key={k.l} className="rounded-lg bg-muted/40 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.l}</p>
                  <p className="truncate text-sm font-semibold text-foreground" title={k.v}>{k.v}</p>
                </div>
              ))}
            </div>
            <table className="w-full table-fixed text-xs">
              <colgroup>
                <col className="w-[16%]" /><col className="w-[24%]" /><col className="w-[12%]" />
                <col className="w-[18%]" /><col className="w-[13%]" /><col className="w-[17%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-border text-left">
                  <th className={th}>Linha</th><th className={th}>Plano atual</th>
                  <th className={cn(th, 'text-right')}>Valor atual</th><th className={th}>Consumo</th>
                  <th className={th}>Fidelidade</th><th className={th}>Serviços</th>
                </tr>
              </thead>
              <tbody>
                {doConta.map((l) => (
                  <tr key={l.numero} className="border-b border-border last:border-0">
                    <td className={cn(td, 'font-semibold tabular-nums')}>{fmtTelefone(l.numero)}</td>
                    <td className={cn(td, 'truncate')} title={l.plano}>{l.plano || '—'}</td>
                    <td className={cn(td, 'text-right tabular-nums')}>{moeda.format(l.valor)}</td>
                    <td className={td}>
                      <span className="tabular-nums">{fmtGb(l.usoGb)} de {fmtGb(l.franquiaGb)}</span>
                      <div className="mt-1 h-1 rounded bg-muted">
                        <div className="h-1 rounded bg-primary" style={{ width: `${l.franquiaGb > 0 ? Math.min(100, (l.usoGb / l.franquiaGb) * 100) : 0}%` }} />
                      </div>
                    </td>
                    <td className={cn(td, l.fidelidade ? 'text-primary' : 'text-muted-foreground')}>
                      {l.fidelidade ? `até ${dataBr(l.fidelidade)}` : 'sem fidelidade'}
                    </td>
                    <td className={cn(td, 'truncate text-muted-foreground')} title={l.servicos.join(', ')}>
                      {l.servicos.join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}

      {(linhas.length > 0 || itensFixa.length > 0) && (
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm font-bold text-foreground">Simulação de renovação</p>
            {onComposicaoChange && (
              <Button size="sm" className="h-8 text-xs" disabled={!completa || jaNaProposta} onClick={levarParaProposta}>
                {jaNaProposta ? 'Já está na proposta' : 'Levar para a proposta'}
                {!jaNaProposta && <ArrowRight className="ml-1.5 h-3.5 w-3.5" />}
              </Button>
            )}
          </div>
          <table className="w-full table-fixed text-xs">
            <colgroup>
              <col className="w-[16%]" /><col className="w-[22%]" /><col className="w-[12%]" />
              <col className="w-[28%]" /><col className="w-[11%]" /><col className="w-[11%]" />
            </colgroup>
            <thead>
              <tr className="border-b border-border text-left">
                <th className={th}>{itensFixa.length > 0 ? 'Item' : 'Linha'}</th><th className={th}>Plano atual</th>
                <th className={cn(th, 'text-right')}>Valor atual</th><th className={th}>Plano novo</th>
                <th className={cn(th, 'text-right')}>Valor novo</th><th className={cn(th, 'text-right')}>Diferença</th>
              </tr>
            </thead>
            <tbody>
              {itensFixa.length > 0 && (
                <tr><td colSpan={6} className={grupo}>Fixa</td></tr>
              )}
              {itensFixa.map((i) => {
                const plano = planoFixaDe(i.servico);
                const novo = retirado(i.servico) ? 0 : plano ? Number(plano.valor) : null;
                const dif = novo == null ? null : novo - Number(i.valor || 0);
                const faltam = mesesAteFimDoDesconto(i);
                return (
                  <tr key={`simfixa-${i.servico}`} className="border-b border-border">
                    <td className={cn(td, 'font-semibold')}>{ROTULO_SERVICO[i.servico]}</td>
                    <td className={td}>
                      <span className="block truncate" title={i.plano || ''}>{i.plano || '—'}</span>
                      {faltam != null && (
                        <span className="text-[10.5px] text-muted-foreground">
                          desconto {i.parcela_atual} de {i.parcelas_total} · depois {moeda.format(valorDepoisDoDesconto(i))}
                        </span>
                      )}
                    </td>
                    <td className={cn(td, 'text-right tabular-nums')}>{moeda.format(Number(i.valor || 0))}</td>
                    <td className={td}>
                      {seletorPlano(
                        escolhasFixa[i.servico] || '',
                        (id) => setEscolhasFixa((a) => ({ ...a, [i.servico]: id })),
                        catalogoFixa[i.servico],
                        plano,
                        true,
                        retirado(i.servico) ? null : modoDoServico(i.servico),
                      )}
                    </td>
                    <td className={cn(td, 'text-right tabular-nums')}>{novo == null ? '—' : moeda.format(novo)}</td>
                    <td className={cn(td, 'text-right font-semibold tabular-nums', dif != null && corDif(dif))}>
                      {dif == null ? '—' : assinado(dif)}
                    </td>
                  </tr>
                );
              })}
              {itensFixa.length > 0 && linhas.length > 0 && (
                <tr><td colSpan={6} className={grupo}>Móvel</td></tr>
              )}
              {linhas.map((l) => {
                const plano = planoDe(l.numero);
                const dif = plano ? Number(plano.valor) - l.valor : null;
                return (
                  <tr key={`sim-${l.numero}`} className="border-b border-border">
                    <td className={cn(td, 'font-semibold tabular-nums')}>{fmtTelefone(l.numero)}</td>
                    <td className={td}>
                      <span className="block truncate" title={l.plano}>{l.plano || '—'}</span>
                      <span className="text-[10.5px] text-muted-foreground">{fmtGb(l.franquiaGb)}</span>
                    </td>
                    <td className={cn(td, 'text-right tabular-nums')}>{moeda.format(l.valor)}</td>
                    <td className={td}>
                      {seletorPlano(
                        escolhas[l.numero] || '',
                        (id) => setEscolhas((a) => ({ ...a, [l.numero]: id })),
                        planos,
                        plano,
                        false,
                        modoMovel,
                      )}
                    </td>
                    <td className={cn(td, 'text-right tabular-nums')}>{plano ? moeda.format(Number(plano.valor)) : '—'}</td>
                    <td className={cn(td, 'text-right font-semibold tabular-nums', dif != null && corDif(dif))}>
                      {dif == null ? '—' : assinado(dif)}
                    </td>
                  </tr>
                );
              })}
              <tr className="font-bold">
                <td className={td}>
                  Total
                  {itensFixa.length === 0 && ` · ${linhas.length} ${linhas.length === 1 ? 'linha' : 'linhas'}`}
                </td>
                <td className={td} />
                <td className={cn(td, 'text-right tabular-nums')}>{moeda.format(totalAtual)}</td>
                <td className={td} />
                <td className={cn(td, 'text-right tabular-nums')}>{completa ? moeda.format(totalNovo) : '—'}</td>
                <td className={cn(td, 'text-right tabular-nums', corDif(totalNovo - totalAtual))}>
                  {completa ? assinado(totalNovo - totalAtual) : '—'}
                </td>
              </tr>
            </tbody>
          </table>
          {comFixa && (
            <div className="mt-3 rounded-lg bg-muted/40 px-3 py-2 text-[11.5px] leading-relaxed text-foreground">
              {temFibra && linhas.length > 0
                ? <p>Tudo no preço de combo porque o cliente fica com fibra e móvel juntos. Se tirar a internet, as linhas voltam ao preço Single e a simulação recalcula sozinha.</p>
                : !temFibra && linhas.length > 0
                  ? <p>Sem a internet, as linhas saem do preço de combo e voltam ao Single.</p>
                  : null}
              {itensComDesconto.length > 0 && completa && (
                <p>
                  {itensComDesconto.map((i) => {
                    const faltam = mesesAteFimDoDesconto(i) as number;
                    return `${faltam === 0 ? 'No próximo mês' : `Daqui a ${faltam} ${faltam === 1 ? 'mês' : 'meses'}`} ${ROTULO_SERVICO[i.servico].toLowerCase()} sobe para ${moeda.format(valorDepoisDoDesconto(i))}`;
                  }).join('; ')}
                  {' '}e a conta de hoje vai a {moeda.format(totalDepois)}:{' '}
                  <span className="font-semibold">{assinado(totalNovo - totalDepois)} por mês</span> contra o que ele vai pagar.
                </p>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
